import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Crown, Sparkles, TrendingUp, Users } from "lucide-react";

export default function Ambassadors() {
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/referrals/leaderboard?limit=50").then((r) => setItems(r.data)),
      api.get("/referrals/tiers").then((r) => setTiers(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const totalRaisedByAmbassadors = items.reduce((s, a) => s + Number(a.total_referred_dollars || 0), 0);

  return (
    <div className="grain min-h-screen max-w-6xl mx-auto px-6 sm:px-10 pt-16 pb-24" data-testid="ambassadors-page">
      <div className="label-eyebrow">The Ambassador Wall</div>
      <h1 className="mt-4 font-serif text-5xl sm:text-6xl text-bdu-cream tracking-tight">
        The people <span className="italic text-bdu-gold">multiplying</span> the movement.
      </h1>
      <p className="mt-6 text-bdu-cream/70 max-w-2xl leading-relaxed">
        Every Ambassador below has personally brought new donors into the Trillion Dollar Spending
        Power Fund. They earn certificates, perks, and — most importantly — a permanent place in the
        story of how we built this.
      </p>

      <div className="mt-8 flex flex-wrap gap-4">
        <Link to="/pledge" className="btn-gold" data-testid="amb-page-cta-pledge"><Sparkles size={16} /> Donate & Credit Someone</Link>
        <Link to="/dashboard" className="btn-ghost" data-testid="amb-page-cta-dashboard">Become an Ambassador</Link>
      </div>

      {/* Stats bar */}
      <div className="mt-14 grid md:grid-cols-3 gap-6">
        <div className="border border-bdu-gold/20 bg-bdu-surface p-6">
          <div className="label-eyebrow flex items-center gap-2"><Users size={12} /> Public Ambassadors</div>
          <div className="mt-3 font-serif text-4xl text-bdu-cream">{items.length}</div>
        </div>
        <div className="border border-bdu-gold/20 bg-bdu-surface p-6">
          <div className="label-eyebrow flex items-center gap-2"><TrendingUp size={12} /> Total They've Raised</div>
          <div className="mt-3 font-serif text-4xl text-bdu-gold">${totalRaisedByAmbassadors.toLocaleString()}</div>
        </div>
        <div className="border border-bdu-gold/20 bg-bdu-surface p-6">
          <div className="label-eyebrow flex items-center gap-2"><Crown size={12} /> Top Tier Reached</div>
          <div className="mt-3 font-serif text-3xl text-bdu-cream">
            {items[0]?.tier?.name || "—"}
          </div>
        </div>
      </div>

      {/* Tier ladder */}
      <div className="mt-16">
        <div className="label-eyebrow">Ambassador Tiers</div>
        <div className="mt-5 grid sm:grid-cols-5 gap-3" data-testid="amb-page-tiers">
          {tiers.map((t) => (
            <div key={t.key} className="border border-bdu-gold/20 bg-bdu-surface p-4">
              <div className="text-xs uppercase tracking-[0.2em] text-bdu-gold">{t.name.replace(" Ambassador", "")}</div>
              <div className="mt-2 font-serif text-xl text-bdu-cream">${t.min.toLocaleString()}</div>
              <ul className="mt-2 space-y-0.5 text-[11px] text-bdu-cream/60">
                {t.perks.slice(0, 3).map((p) => <li key={p}>· {p}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Leaderboard */}
      <div className="mt-16">
        <h2 className="font-serif text-3xl text-bdu-cream">The Roll</h2>
        <div className="mt-4 divider-gold" />
        {loading ? (
          <div className="text-bdu-cream/50 py-12 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="mt-10 text-center py-14 border border-bdu-gold/15" data-testid="amb-page-empty">
            <div className="font-serif text-2xl text-bdu-cream">No public Ambassadors yet.</div>
            <p className="mt-3 text-bdu-cream/60 max-w-md mx-auto">
              Be the first to claim your spot. Share your code, bring in donations, opt into the public wall — and this page will start filling up with names.
            </p>
            <Link to="/dashboard" className="btn-gold mt-8 inline-flex">Get My Ambassador Code</Link>
          </div>
        ) : (
          <ul className="mt-8 space-y-3">
            {items.map((a) => (
              <li
                key={a.rank}
                className="border border-bdu-gold/15 bg-bdu-surface p-5 flex items-center justify-between gap-4 flex-wrap"
                data-testid={`amb-row-${a.rank}`}
              >
                <div className="flex items-center gap-5 flex-1 min-w-0">
                  <div className={`shrink-0 w-14 h-14 flex items-center justify-center font-serif text-2xl border ${a.rank <= 3 ? "border-bdu-gold text-bdu-gold" : "border-bdu-gold/25 text-bdu-cream/60"}`}>
                    {a.rank}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif text-xl sm:text-2xl text-bdu-cream truncate">{a.name}</span>
                      {a.rank === 1 && <Crown size={16} className="text-bdu-gold" />}
                      {a.is_member && (
                        <span className="inline-flex items-center gap-1 border border-bdu-gold/50 bg-bdu-gold/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] text-bdu-gold">
                          <Crown size={9} /> Founding Member
                        </span>
                      )}
                    </div>
                    {a.tier && (
                      <div className="mt-1 text-[11px] uppercase tracking-[0.25em] text-bdu-gold">{a.tier.name}</div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-bdu-gold font-serif text-2xl tabular-nums">${Number(a.total_referred_dollars).toLocaleString()}</div>
                  <div className="text-[11px] text-bdu-cream/50 tracking-widest uppercase">
                    {a.referral_count} referral{a.referral_count === 1 ? "" : "s"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Legal disclosure */}
      <p className="mt-16 text-[11px] text-bdu-cream/40 leading-relaxed max-w-3xl">
        Black Dollars United is the <span className="text-bdu-cream/60">nonprofit</span> movement.
        The <span className="text-bdu-cream/60">Trillion Dollar Spending Power Fund</span> is a separate entity with its own
        management team and Board of Directors. Ambassador certificates and perks are symbolic recognitions of contribution —
        they are not equity, ownership, securities, or promises of financial return.
      </p>
    </div>
  );
}
